/**
 * The overlay: dim the page, cut a hole around the real button, explain it in a sentence, advance
 * when they actually click it.
 *
 * NO NEW DEPENDENCY. driver.js/shepherd/intro.js were all rejected for this: the useful part is a
 * few hundred lines, and the parts that matter here — per-tenant brand colour, a focus ring that
 * includes the anchored element, a spotlight that survives a lazy-loaded route change, a MISSING
 * anchor failing loudly instead of quietly — are exactly the parts a library would make us fight.
 *
 * THE DIM IS FOUR RECTANGLES, NOT ONE WITH A HOLE. A full-screen element with a CSS-punched hole
 * still sits over the button and swallows the click, which would kill click-to-advance outright.
 * Four panels around the target leave the target genuinely uncovered, so the click lands on the real
 * button, runs the real handler, and the app does the real thing. That is the entire premise of the
 * feature: this is not a slideshow of screenshots.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { X, ChevronLeft, ChevronRight, MousePointerClick, AlertTriangle, ShieldCheck, HelpCircle } from 'lucide-react'

import { useTour } from './tourContext'
import { useAnchorElement, useElementRect, ANCHOR_STATUS } from './useAnchorElement'
import { TOUR_STRICT, isActionStep } from './tourSchema'
import { useStepGate } from './useStepGate'
import { gateSignal, watchUnanswered, isTypedInto } from './gateDetectors'

/**
 * The questions a card is still waiting on, live.
 *
 * A card that gates on a whole section has to say WHICH box is still empty, or it is a locked door
 * with no sign on it: "fill in this section" is useless on a section with seven boxes and one blank,
 * and the reader's only way out is the fifteen-second stuck timer. The list is recomputed from the
 * screen itself (`data-tour-answered`, see tourAnchors.js) rather than tracked, so it shrinks as they
 * work and disappears at the moment the tour moves on.
 */
function useUnanswered(step, enabled) {
  // Carries the step it describes and is compared DURING RENDER — the same shape, and the same
  // reason, as useStepGate's satisfaction and useAnchorElement's status. A state update lands one
  // render after the prop that caused it, so without the id this card would spend one frame naming
  // the PREVIOUS card's blank boxes: the reader's eye is on this panel at exactly the moment the tour
  // moves, which is the worst possible moment to show them a list that belongs to the step they have
  // just finished.
  const [state, setState] = useState({ stepId: null, outstanding: [] })
  const signal = gateSignal(step && step.until)
  const target = signal === 'answered' ? step.until.answered : null
  const stepId = step ? step.id : null

  useEffect(() => {
    if (!enabled || !target) { setState({ stepId, outstanding: [] }); return undefined }
    return watchUnanswered(target, (outstanding) => setState({ stepId, outstanding }))
    // `until` is frozen by defineTour and identity-stable per step, so stepId is the honest key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepId, enabled, !!target])

  return state.stepId === stepId ? state.outstanding : []
}

/**
 * The `tour` rung of shared/styles/zLayers.js: above the header (200), above every modal (1000),
 * dialog (1200), menu (2000) and toast (3000), and BELOW AnnouncementModal's `blocking` 12000 —
 * that modal is an acknowledgement the user must clear and should win.
 *
 * The old note here recorded a known limit: `components/Modal.jsx` used 999999 and a tour step
 * pointing inside it was covered. That is fixed — Modal.jsx is on the `modal` rung now.
 *
 * Kept a literal because StickerPrintSheet.zIndexBeatsTheHeader.test.jsx reads it back with a
 * regex; zLayers.contract.test.jsx fails if this number and the rung disagree.
 */
const Z = 11000

/** Breathing room between the highlighted element's box and the edge of the lit area. */
const HALO = 6
const CARD_WIDTH = 340
const GAP = 12
/** Keep the card off the very edge of the screen. */
const EDGE = 16
/**
 * Used for ONE paint, before the card has been measured, and as the floor when there is genuinely no
 * room anywhere. A card is never PLACED on the assumption that it is this tall — that assumption is
 * what used to drop a 294px card on top of the Submit button it was pointing at.
 */
const CARD_HEIGHT_GUESS = 200
const MIN_CARD_HEIGHT = 120

/**
 * The dim goes AMBER during a practice run rather than slate.
 *
 * It is one of three markers, and the reason there are three is that the failure they prevent is
 * severe out of all proportion to their cost: someone who believes they filed a real daily report
 * and did not is worse off than someone who never took the tour. The colour is the ambient one you
 * cannot look past, the card carries the sentence, and the badge survives even when the card is
 * scrolled to a corner of a phone screen.
 */
const panelStyle = (practice, extra) => ({
  position: 'fixed',
  background: practice ? 'rgba(69, 26, 3, 0.58)' : 'rgba(15, 23, 42, 0.55)',
  zIndex: Z,
  ...extra,
})

// One dim panel per side of the lit area. The testid is how the suite asserts the rule that matters
// most here: when nothing is lit, nothing is dimmed.
const Dim = ({ practice, ...props }) => (
  <div data-testid="tour-dim" aria-hidden="true" style={panelStyle(practice, props)} />
)

function viewport() {
  return {
    w: typeof window === 'undefined' ? 0 : window.innerWidth,
    h: typeof window === 'undefined' ? 0 : window.innerHeight,
  }
}

/**
 * Card sits under the spotlight when it FITS under it, over it when it fits over it, and shrinks
 * into whichever side has more room when it fits in neither.
 *
 * `cardHeight` is the card's MEASURED height and the reason this function takes an argument at all.
 * Placing a card by an assumed height is placing it wrong: the card is a clickable surface at
 * z 11002, so a card that overlaps its own anchor does not merely look untidy — it eats the click
 * that click-to-advance depends on, and the last three steps of the daily-report tour anchor buttons
 * in a bar that sits 30px off the bottom of a tablet screen.
 *
 * The returned `maxHeight` is what keeps the card ON the screen. It is position:fixed, so a footer
 * pushed below the fold takes Back/Next with it and there is nothing the user can scroll.
 */
function cardPosition(rect, cardHeight) {
  const { w, h } = viewport()
  const width = Math.min(CARD_WIDTH, Math.max(240, w - 32))
  const cap = Math.max(MIN_CARD_HEIGHT, h - EDGE * 2)
  const height = Math.min(cardHeight || CARD_HEIGHT_GUESS, cap)

  if (!rect || !rect.measurable) {
    return { width, left: Math.max(EDGE, (w - width) / 2), top: Math.max(EDGE, (h - height) / 2), maxHeight: cap }
  }

  const below = rect.bottom + HALO + GAP
  const roomBelow = h - below - EDGE
  const roomAbove = rect.top - HALO - GAP - EDGE

  let top
  let maxHeight
  if (height <= roomBelow) {
    top = below
    maxHeight = roomBelow
  } else if (height <= roomAbove) {
    top = rect.top - HALO - GAP - height
    maxHeight = roomAbove
  } else {
    // Neither side fits the whole card, so it scrolls internally in the roomier one rather than
    // covering the control. MIN_CARD_HEIGHT is the floor: below it the card is unreadable, and a
    // screen with less room than that on either side of the anchor has no good answer.
    maxHeight = Math.max(MIN_CARD_HEIGHT, Math.max(roomAbove, roomBelow))
    top = roomAbove > roomBelow ? Math.max(EDGE, rect.top - HALO - GAP - maxHeight) : below
  }

  const shown = Math.min(height, maxHeight)
  top = Math.max(EDGE, Math.min(top, h - shown - EDGE))
  // maxHeight is what the BROWSER clamps to, and until this line it could exceed the room actually
  // left below `top` — because `top` is placed from the card's height at the moment of measuring,
  // while maxHeight described the room on that side of the anchor. On a 360×640 phone with the
  // anchor near the bottom that produced top 342 + maxHeight 526, i.e. a licence to draw to 868px on
  // a 640px screen. It only bit when the card GREW after being placed, which is rare — and stopped
  // being rare the moment a gated step started growing a "Can’t do this one?" panel into itself
  // fifteen seconds in. A fixed-position card whose footer is below the fold has taken Back, the
  // step counter and the way out of the tour with it, and there is nothing the user can scroll.
  maxHeight = Math.max(MIN_CARD_HEIGHT, Math.min(maxHeight, h - top - EDGE))
  const left = Math.min(Math.max(EDGE, rect.left + rect.width / 2 - width / 2), Math.max(EDGE, w - width - EDGE))
  return { width, left, top, maxHeight }
}

/**
 * The badge that says PRACTICE even when the card is somewhere else on the screen.
 *
 * pointerEvents:'none' is load-bearing, not tidiness: this is a fixed strip near the top of the
 * viewport, which is exactly where the nav tabs several tour steps point at live. A marker that ate
 * a click on the control the step is telling you to press would be the tour breaking the tour.
 */
const PracticeBadge = () => (
  <div
    data-testid="tour-practice-badge"
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: Z + 3,
      pointerEvents: 'none',
      display: 'flex',
      justifyContent: 'center',
    }}
  >
    <div
      className="flex items-center gap-1.5 px-3 py-1 text-xs font-bold uppercase tracking-wide text-amber-950 rounded-b-lg shadow"
      style={{ background: 'rgb(252 211 77)' }}
    >
      <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" />
      Practice run — nothing is saved
    </div>
  </div>
)

/**
 * The card shown once a practice run is over.
 *
 * It states the outcome in plain words and it says practice mode is OFF, because the moment after a
 * training exercise is precisely when somebody is most likely to keep clicking and assume the next
 * thing is not real either.
 */
function PracticeSummary({ finished, onDismiss }) {
  const { w, h } = viewport()
  const width = Math.min(380, Math.max(260, w - 32))
  return (
    <div
      role="dialog"
      aria-labelledby="tour-finished-title"
      data-testid="tour-finished"
      className="fixed bg-white rounded-xl shadow-2xl border border-amber-300 p-4"
      style={{ zIndex: Z + 2, width, left: Math.max(EDGE, (w - width) / 2), top: Math.max(EDGE, h / 2 - 110) }}
    >
      <h2 id="tour-finished-title" className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
        <ShieldCheck className="w-4 h-4 text-amber-600" aria-hidden="true" />
        {finished.reason === 'completed' ? 'Practice run finished' : 'Practice run stopped'}
      </h2>
      <p className="mt-2 text-sm text-gray-700 leading-relaxed">
        Nothing you just did was saved. No record was created — no daily report, no ticket, no time
        entry, no sticker code and no email.
        {finished.blocked > 0 && (
          <> We stopped {finished.blocked} {finished.blocked === 1 ? 'save' : 'saves'} from reaching the server.</>
        )}
      </p>
      <p className="mt-2 text-sm font-semibold text-gray-900">
        Practice mode is now off. Anything you do from here is real.
      </p>
      <div className="mt-3 flex justify-end border-t border-gray-100 pt-3">
        <button
          type="button"
          onClick={onDismiss}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-bfs-maroon hover:opacity-90"
        >
          Got it
        </button>
      </div>
    </div>
  )
}

export default function TourOverlay({ anchorTimeoutMs = 4000, strict = TOUR_STRICT }) {
  const {
    active, step, stepNumber, stepCount, next, back, stop, skipUnavailable,
    practice, finished, dismissFinished, stuckAfterMs,
  } = useTour()
  const location = useLocation()
  const cardRef = useRef(null)

  const { status, element, count } = useAnchorElement(step ? step.anchor : null, {
    enabled: !!step,
    timeoutMs: anchorTimeoutMs,
  })
  const rect = useElementRect(status === ANCHOR_STATUS.FOUND ? element : null)

  const broken = status === ANCHOR_STATUS.MISSING || status === ANCHOR_STATUS.AMBIGUOUS

  // ── The anchor is gone ────────────────────────────────────────────────────────────────────────
  // In dev and in CI this is a red card the author cannot miss; the same condition is what
  // __tests__/tourAnchors.contract.test.jsx turns into a failing build, so it should never survive
  // to a release. In production it is a shrug: skip the step and keep the tour useful, because a
  // new hire cannot act on our broken anchor and must never be shown a dimmed screen with nothing
  // lit in it.
  useEffect(() => {
    if (!step || !broken || strict) return
    skipUnavailable()
  }, [step, broken, strict, skipUnavailable])

  useEffect(() => {
    if (!step || !broken || !strict) return
    console.error(
      `[tour] step "${step.id}" of tour "${active && active.tour.id}" points at ` +
      `data-tour="${step.anchor}", which ${status === ANCHOR_STATUS.AMBIGUOUS
        ? `matches ${count} elements — an anchor must be unique`
        : 'is not on this page'}.`,
    )
  }, [step, broken, strict, status, count, active])

  // Scroll the target into view once per step. Not on every rect change — that would fight the user
  // the moment they scroll away to read something.
  useEffect(() => {
    if (status !== ANCHOR_STATUS.FOUND || !element) return
    try {
      element.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' })
    } catch {
      // jsdom, and older Safari with an options object. Position is still correct; only the
      // convenience scroll is lost.
    }
  }, [status, element])

  // ── The gate: they DO it, and the tour follows ────────────────────────────────────────────────
  //
  // "It should be a guided tour. not something a use can spam next on to get through." An action
  // step therefore advances on ONE thing — the person doing it — and Next is not a second way past.
  //
  // The watch is disabled while the anchor is broken. A step whose target does not exist cannot be
  // performed, and the broken-anchor paths above (skip in production, shout in dev) own that case;
  // leaving a `gone`-style gate armed against a page it was never on would satisfy it for a reason
  // that has nothing to do with the user.
  const gated = isActionStep(step)
  const gateSatisfied = useStepGate(step, { enabled: gated && !broken, pathname: location.pathname })
  const outstanding = useUnanswered(step, gated && !broken)

  useEffect(() => {
    if (!gateSatisfied) return
    next()
  }, [gateSatisfied, next])

  // ── Never a dead end ──────────────────────────────────────────────────────────────────────────
  //
  // A gated step CAN be impossible: the control is disabled, the tenant has no machines to pick yet,
  // a dependency is 500ing. Trapping somebody in a tour they can neither finish nor leave is worse
  // than the spammable tour this replaced, so after a wait the step admits it and offers a way past
  // WITH AN HONEST REASON. Not immediately — offering a skip on arrival is just the slideshow again,
  // and the overwhelmingly common reason a gate is unsatisfied one second in is that the person is
  // still reading.
  //
  // IT MEASURES A LACK OF PROGRESS, NOT TIME ON THE CARD, and that distinction arrived with the
  // section gates. Fifteen seconds is a fair judgement of "this person cannot press that button"; it
  // is a poor one about a card asking for six answers, which a careful foreman will not finish in
  // fifteen seconds and should not — the JSA's emergency section is a conversation with the crew, not
  // a typing exercise. Offering him "Skip this step" halfway down it is the slideshow reappearing at
  // the worst possible moment: the panel arrives while he is working, on the card that exists
  // BECAUSE those boxes were being skipped.
  //
  // So every answer restarts the clock. `outstanding.length` is 0 and unchanging for every other kind
  // of gate, so nothing else in any tour behaves differently.
  const [stuck, setStuck] = useState(false)
  const outstandingCount = outstanding.length
  useEffect(() => {
    setStuck(false)
    if (!step || !gated) return undefined
    const t = setTimeout(() => setStuck(true), stuckAfterMs)
    return () => clearTimeout(t)
  }, [step, gated, stuckAfterMs, outstandingCount])

  const stuckReason = useMemo(() => {
    if (!step) return null
    if (broken) return `We cannot find that control on this page (${step.anchor}).`
    if (element && element.disabled) {
      return 'The control this step points at is greyed out, so it cannot be clicked yet — you may '
        + 'not have permission for it, or something above it still needs filling in.'
    }
    if (gateSignal(step.until) === 'route') return `We are waiting for you to reach ${step.until.route}.`
    return null
  }, [step, broken, element])

  // ── Escape + focus trap ───────────────────────────────────────────────────────────────────────
  // The trap deliberately includes the ANCHORED ELEMENT alongside the card's own buttons. A keyboard
  // user has to be able to reach the thing the tour is pointing at — trapping focus inside the card
  // would make click-to-advance mouse-only, which is worse than no trap at all. So the tab ring is
  // [anchor, Back, Next, Skip] and Tab cycles it.
  const focusRing = useCallback(() => {
    const card = cardRef.current
    const inCard = card
      ? Array.from(card.querySelectorAll('button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
      : []
    const anchorFirst = element && !broken && typeof element.focus === 'function' ? [element] : []
    return [...anchorFirst, ...inCard]
  }, [element, broken])

  // BOTH KEYS ARE HANDLED ONLY WHEN THE USER IS ADDRESSING THE TOUR — i.e. when focus is inside the
  // card. This listener is on `document` with capture, which is the FIRST node in the propagation
  // path, so anything it consumes never reaches the page at all. The page is not a backdrop here:
  // the user is operating its real controls, and 28 places in src/ handle Escape themselves
  // (SearchableSelect closes its option list, StickerPrintSheet closes a print preview — both of
  // them inside screens a tour step deliberately parks on). Claiming Escape globally meant one
  // keypress killed the tour AND left the dropdown open; claiming Tab globally meant a keyboard user
  // could not move between the fields of the very form the step was telling them to fill in.
  useEffect(() => {
    if (!step) return undefined
    const onKeyDown = (e) => {
      if (e.key !== 'Escape' && e.key !== 'Tab') return
      const card = cardRef.current
      const inCard = !!card && card.contains(document.activeElement)

      if (e.key === 'Escape') {
        // Not ours — let the app have it. The card's X is the visible, keyboard-reachable exit, and
        // it is the first stop in the ring below.
        if (!inCard) return
        e.stopPropagation()
        stop('dismissed')
        return
      }

      const ring = focusRing()
      if (ring.length === 0) return
      const current = ring.indexOf(document.activeElement)
      // Focus is somewhere in the page: the ring's job is to make the card and its anchor reachable,
      // not to make everything else unreachable. Let the browser's own tab order run.
      if (current === -1 && !inCard) return
      const delta = e.shiftKey ? -1 : 1
      const nextIndex = current === -1 ? 0 : (current + delta + ring.length) % ring.length
      e.preventDefault()
      ring[nextIndex].focus()
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [step, stop, focusRing])

  // ── Move focus to the card when a step opens — UNLESS SOMEBODY IS WRITING ─────────────────────
  //
  // The focus move is for a screen-reader user: a new card is a new instruction and nothing announces
  // it otherwise. The exception is not a nicety. A card can open while the person is mid-sentence —
  // that is exactly what a gate over a typed answer does, since the box holds an answer after one
  // character — and a browser routes keystrokes to document.activeElement. Pulling focus here threw
  // away everything typed after that first character, leaving "B" in `scope_of_work` and one letter
  // in the hazard table's first cell, on a sheet the walkthrough then declared finished. It was
  // measured on the JSA at ordinary typing speed.
  //
  // gateDetectors' settle window means the card should no longer arrive mid-word at all. This is the
  // guarantee underneath it rather than a duplicate of it: a pause long enough to end that window
  // (thinking, being spoken to, a slow phone) must still not cost somebody the rest of their
  // sentence, and no future gate should be able to reintroduce that by moving faster.
  //
  // Nothing is lost for the screen-reader case it exists for: a person who is typing is a person who
  // is already reading the sheet, and the card's own aria-live outstanding list still speaks.
  useEffect(() => {
    if (!step || !cardRef.current) return
    const writingElsewhere = typeof document !== 'undefined'
      && isTypedInto(document.activeElement)
      && !cardRef.current.contains(document.activeElement)
    if (writingElsewhere) return
    cardRef.current.focus()
  }, [step])

  // ── Measure the card, then place it ───────────────────────────────────────────────────────────
  // Two passes: the first paint uses CARD_HEIGHT_GUESS, this effect reads the real height, and the
  // re-render places it properly. Step bodies run from one line to eight, so no constant is right
  // for all of them.
  //
  // scrollHeight, not just the box, because the box is the thing we CLAMP. Feeding a clamped height
  // back into the placement that produced the clamp is how a two-pass layout starts oscillating;
  // scrollHeight is the card's natural content height and does not move when maxHeight bites. The
  // rect is the fallback for jsdom, which reports 0 for every scroll metric.
  const [cardHeight, setCardHeight] = useState(0)
  // No dependency list ON PURPOSE: the card's height is a function of everything that renders inside
  // it — the step's copy, the broken-anchor banner, the "click it to continue" line, the width the
  // viewport allows — and listing those is a list that silently rots the first time someone adds a
  // line to the card. Measuring after every render is safe because the write is CONDITIONAL and the
  // measured value is stable (see scrollHeight above): it settles in at most two renders and then
  // stops, so there is no update loop for the rule to protect against.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const el = cardRef.current
    if (!el) return
    const natural = Math.ceil(Math.max(el.scrollHeight || 0, el.getBoundingClientRect().height || 0))
    setCardHeight((h) => (natural > 0 && natural !== h ? natural : h))
  })

  const pos = useMemo(() => cardPosition(rect, cardHeight), [rect, cardHeight])

  // The tour is over. The card that says so is the LAST thing a practice run does, and it is not
  // optional — see PracticeSummary.
  if (!step) return finished ? <PracticeSummary finished={finished} onDismiss={dismissFinished} /> : null

  const isFirst = stepNumber <= 1
  const isLast = stepNumber >= stepCount
  const offTrack = step.route && location.pathname !== step.route
  const signal = gateSignal(step.until)
  /** What this step is waiting for, in the second person. */
  const WAITING_FOR = {
    clicked: 'Click it to continue.',
    appears: 'Do it, and the tour will follow.',
    gone: 'Close it, and the tour will follow.',
    filled: 'Fill it in, and the tour will follow.',
    // Not "fill it in": this one waits for an ANSWER, and on a picker those are different acts —
    // typing a name is not choosing one. The list below names what is left.
    answered: outstanding.length > 1
      ? `Answer all ${outstanding.length}, and the tour will follow.`
      : 'Answer it, and the tour will follow.',
    route: 'Go there, and the tour will follow.',
    requested: 'Do it, and the tour will follow.',
  }

  const card = (
    <div
      ref={cardRef}
      role="dialog"
      // NOT aria-modal: the whole point is that the rest of the page stays reachable, because the
      // user is meant to operate the highlighted control. aria-modal would tell a screen reader the
      // opposite and hide it.
      aria-labelledby="tour-step-title"
      aria-describedby="tour-step-body"
      tabIndex={-1}
      data-testid="tour-card"
      className="fixed bg-white rounded-xl shadow-2xl border border-gray-200 p-4 outline-none"
      style={{
        zIndex: Z + 2,
        width: pos.width,
        left: pos.left,
        top: pos.top,
        // The card scrolls itself when it cannot fit. Losing its own Back/Next off the bottom of a
        // fixed-position element is not recoverable by the user.
        maxHeight: pos.maxHeight,
        overflowY: 'auto',
      }}
    >
      {/* Marker two of three. The card is the surface the person is actually reading, so the
          sentence lives here rather than only in the ambient colour. */}
      {practice && (
        <div className="-mx-4 -mt-4 mb-3 px-4 py-1.5 rounded-t-xl bg-amber-100 border-b border-amber-300 text-[11px] font-bold uppercase tracking-wide text-amber-900 flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" /> Practice — nothing is saved
        </div>
      )}

      {/* A RESUMED practice run starts its record afresh (TourProvider, `resumeFrom`), so the first card
          of a resumed run says so — otherwise "your PDF is gone" reads as the app having lost it. */}
      {active && active.resumed && active.tour.resumeNote ? (
        <p data-testid="tour-resume-note" className="mb-2 rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-[11px] text-amber-900">
          {active.tour.resumeNote}
        </p>
      ) : null}
      <div className="flex items-start justify-between gap-2 mb-1">
        <h2 id="tour-step-title" className="text-sm font-bold text-gray-900">{step.title}</h2>
        {/* ALWAYS here, on every step including a gated one. A gate with no exit is a trap, and the
            person who most needs the exit is the one who cannot satisfy the gate. */}
        <button
          type="button"
          onClick={() => stop('dismissed')}
          aria-label="Skip tour"
          className="text-gray-400 hover:text-gray-600 shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <p id="tour-step-body" className="text-sm text-gray-600 leading-relaxed">{step.body}</p>

      {broken && strict && (
        <div className="mt-3 rounded-lg border border-red-300 bg-red-50 p-2.5 text-xs text-red-800">
          <div className="flex items-center gap-1.5 font-semibold mb-1">
            <AlertTriangle className="w-3.5 h-3.5" /> This step is broken
          </div>
          <p>
            {status === ANCHOR_STATUS.AMBIGUOUS
              ? `${count} elements carry data-tour="${step.anchor}". An anchor has to be unique or the tour highlights whichever one the DOM hands back first.`
              : `Nothing on this page carries data-tour="${step.anchor}".`}
          </p>
          <p className="mt-1 text-red-700">
            tour <code>{active.tour.id}</code> · step <code>{step.id}</code>
            {offTrack ? ` · this step lives on ${step.route}, you are on ${location.pathname}` : ''}
          </p>
          <p className="mt-1 text-red-700">
            You are seeing this because it is a development build. A release skips the step instead —
            and src/shared/tour/__tests__/tourAnchors.contract.test.jsx should have failed CI first.
          </p>
        </div>
      )}

      {!broken && gated && (
        <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-gray-500">
          <MousePointerClick className="w-3.5 h-3.5" aria-hidden="true" />
          {WAITING_FOR[signal] || WAITING_FOR.clicked}
        </p>
      )}

      {/* ── WHAT IS STILL MISSING, NAMED ────────────────────────────────────────────────────────
          Immediately, not after the stuck timer: this is not an apology for a dead end, it is the
          card doing its job. A step that holds somebody until a section is answered has to say which
          box is blank, and it has to keep saying it as the section fills up — otherwise the reader is
          hunting a blank box on a sheet of thirty, which is the moment people give up and press
          Skip. `aria-live` because for a screen-reader user this list IS the progress indicator. */}
      {!broken && gated && outstanding.length > 0 && (
        <div
          data-testid="tour-outstanding"
          aria-live="polite"
          className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900"
        >
          <span className="font-semibold">
            {outstanding.length === 1 ? 'Still to answer: ' : `Still to answer (${outstanding.length}): `}
          </span>
          {outstanding.map((o) => o.label).join(' · ')}
        </div>
      )}

      {/* ── The way past, after a wait ─────────────────────────────────────────────────────────
          Deliberately NOT the Next button and deliberately not styled like the primary action: the
          point is to release somebody who is stuck, not to re-offer the slideshow to somebody who
          simply has not got round to it yet. */}
      {gated && stuck && (
        <div data-testid="tour-stuck" className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs text-gray-700">
          <div className="flex items-center gap-1.5 font-semibold mb-1 text-gray-800">
            <HelpCircle className="w-3.5 h-3.5" aria-hidden="true" /> Can’t do this one?
          </div>
          {stuckReason && <p className="mb-1">{stuckReason}</p>}
          {step.stuckHint && <p className="mb-1">{step.stuckHint}</p>}
          <button
            type="button"
            onClick={next}
            className="mt-1 underline font-semibold text-gray-800 hover:text-black"
          >
            Skip this step
          </button>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-2 border-t border-gray-100 pt-3">
        <span className="text-xs text-gray-400 tabular-nums">{stepNumber} of {stepCount}</span>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={back}
            disabled={isFirst}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </button>
          {/* DISABLED on an action step, and that single attribute is the whole of the owner's
              complaint. Not hidden: a person who has just been told to click something needs to see
              that Next exists and is waiting on them, rather than wonder where the button went. */}
          <button
            type="button"
            onClick={next}
            disabled={gated}
            title={gated ? 'Do the step above and the tour will move on by itself' : undefined}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-bfs-maroon hover:opacity-90 disabled:opacity-40 disabled:hover:opacity-40 disabled:cursor-not-allowed"
          >
            {isLast ? 'Done' : 'Next'} <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  )

  // Nothing lit → nothing dimmed. Dimming the page around a spotlight that is 0×0 in the top-left
  // corner is the exact failure this feature is not allowed to have.
  const litUp = !broken && rect && rect.measurable

  return (
    <>
      {/* Marker three of three, and the one that survives the card being pushed into a corner of a
          phone screen or scrolled inside itself. */}
      {practice && <PracticeBadge />}
      {litUp && (
        <>
          <Dim practice={practice} top={0} left={0} right={0} height={Math.max(0, rect.top - HALO)} />
          <Dim practice={practice} top={Math.max(0, rect.bottom + HALO)} left={0} right={0} bottom={0} />
          <Dim practice={practice} top={Math.max(0, rect.top - HALO)} left={0} width={Math.max(0, rect.left - HALO)} height={rect.height + HALO * 2} />
          <Dim practice={practice} top={Math.max(0, rect.top - HALO)} left={rect.right + HALO} right={0} height={rect.height + HALO * 2} />
          <div
            aria-hidden="true"
            data-testid="tour-spotlight"
            style={{
              position: 'fixed',
              zIndex: Z + 1,
              // pointer-events:none is what keeps the highlighted button clickable through the ring.
              pointerEvents: 'none',
              top: rect.top - HALO,
              left: rect.left - HALO,
              width: rect.width + HALO * 2,
              height: rect.height + HALO * 2,
              borderRadius: 8,
              boxShadow: '0 0 0 3px rgb(var(--brand-primary) / 0.9), 0 0 0 6px rgba(255,255,255,0.55)',
            }}
          />
        </>
      )}
      {card}
    </>
  )
}
